import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, ColorPicker, InputNumber, Form, Space, Select, Divider, notification } from 'antd';

import FormHeader from '@controls/form-header/form-header';

import { exception } from '@extensions/notification';
import { NoColor } from '@core/types';

import { userLoaded } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';

import { serverFetch } from '@flc/server';

import { ICity } from '@entities/city';
import { IUserSession } from '@entities/user-session';
import { ITransportTariff } from '@entities/transport-tariff';
import { ITransportTariffLine } from '@entities/transport-tariff-line';
import { IItem } from '@entities/item';

import { Permission, hasPermission } from '@enums/permission';

const TransportTariff = () => {
    const { id, isCopy } = useParams();

    const navigate = useNavigate();
    const [api, contextHolder] = notification.useNotification();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [entity, setEntity] = useState<ITransportTariff>();

    const [cities, setCities] = useState<Array<ICity>>([]);

    const [loading, setLoading] = useState<boolean>(false);
    const [canManage] = useState<boolean>(hasPermission(userSession.permissions, Permission.ManageTradingPlatforms));

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch('items', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения продуктов', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('cities', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            if (id) {
                promises.push(
                    await serverFetch(`transporttariffs/${id}`, { method: 'GET' })
                        .then((data) => {
                            return data;
                        })
                        .catch((ex) => {
                            exception(api, 'Ошибка получения транспортного тарифа', ex, () => d(userLoaded(undefined)));
                        }),
                );
            }

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                const items: Array<IItem> = result[0][0];

                setCities(result[0][1]);

                if (id) {
                    let data = result[0][2] as ITransportTariff;
                    if (isCopy) {
                        data.id = undefined;
                        data.cityId = undefined;
                    }

                    const lines: Array<ITransportTariffLine> = [];
                    const storedLines: Array<ITransportTariffLine> = [...data.lines];
                    items.map((i) => {
                        const storedLine = storedLines.find((l) => l.itemId == i.id);
                        if (storedLine) {
                            storedLine.itemName = i.name;
                            lines.push(storedLine);
                        } else {
                            const newLine: ITransportTariffLine = { itemId: i.id, itemName: i.name, price: undefined };
                            lines.push(newLine);
                        }
                    });

                    data.lines = lines;

                    setEntity(data);
                } else {
                    let data = {
                        id: undefined,
                        cityId: undefined,
                        palletPrice: undefined,
                        weightPrice: undefined,
                        color: NoColor,
                        lines: items.map((i) => {
                            const newLine: ITransportTariffLine = { itemId: i.id, itemName: i.name, price: undefined };
                            return newLine;
                        }),
                    };

                    setEntity(data);
                }

                setLoading(false);
            });
        };

        fetchData();
        return () => {
            cleanup = true;
        };
    }, []);

    const onSave = () => {
        setLoading(true);

        serverFetch(`transporttariffs`, { method: id && !isCopy ? 'PUT' : 'POST', bodyData: entity })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка сохранения транспортного тарифа', ex, () => d(userLoaded(undefined)));
            });
    };

    return (
        <>
            <FormHeader
                title={`${
                    id && !isCopy
                        ? !canManage
                            ? 'Информация о транспортном тарифе'
                            : 'Изменить транспортный ариф'
                        : 'Добавить транспортный тариф'
                }`}
            />
            {!loading && entity && (
                <Form colon={false} labelCol={{ span: 3 }} wrapperCol={{ span: 7 }} onFinish={onSave}>
                    <Form.Item
                        initialValue={entity.cityId}
                        required
                        label='Расчетный город'
                        name='cityId'
                        rules={[{ required: true, message: 'Выберите город' }]}
                    >
                        <Select
                            autoFocus
                            disabled={!canManage}
                            onChange={(value) => setEntity({ ...entity, cityId: value })}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={cities.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        ></Select>
                    </Form.Item>

                    {entity.lines.map((l) => {
                        return (
                            <Form.Item initialValue={l.price} label={`Тариф "${l.itemName}"`} name={l.itemId}>
                                <InputNumber
                                    style={{ width: 150 }}
                                    decimalSeparator=','
                                    stringMode={true}
                                    disabled={!canManage}
                                    onChange={(value: number | null) => {
                                        const copyLines = [...entity.lines];
                                        const line = copyLines.find((s) => s.itemId == l.itemId);
                                        if (line) line.price = value;

                                        setEntity({ ...entity, lines: copyLines });
                                    }}
                                />
                            </Form.Item>
                        );
                    })}

                    <Form.Item initialValue={entity.color || ''} required label='Цвет' name='color'>
                        <ColorPicker
                            format='hex'
                            allowClear
                            styles={{ popupOverlayInner: { width: 492 } }}
                            //value={value}
                            onChangeComplete={(color) => {
                                setEntity({ ...entity, color: color.toHexString() });
                            }}
                            presets={[
                                {
                                    label: 'Рекомендованные',
                                    colors: [
                                        '#F5222D',
                                        '#FA8C16',
                                        '#FADB14',
                                        '#8BBB11',
                                        '#52C41A',
                                        '#13A8A8',
                                        '#1677FF',
                                        '#2F54EB',
                                        '#722ED1',
                                        '#EB2F96',
                                        '#F5222D4D',
                                        '#FA8C164D',
                                        '#FADB144D',
                                        '#8BBB114D',
                                        '#52C41A4D',
                                        '#13A8A84D',
                                        '#1677FF4D',
                                        '#2F54EB4D',
                                        '#722ED14D',
                                        '#EB2F964D',
                                    ],
                                },
                            ]}
                            panelRender={(_, { components: { Picker, Presets } }) => (
                                <div
                                    className='custom-panel'
                                    style={{
                                        display: 'flex',
                                        width: 468,
                                    }}
                                >
                                    <div
                                        style={{
                                            flex: 1,
                                        }}
                                    >
                                        <Presets />
                                    </div>
                                    <Divider
                                        type='vertical'
                                        style={{
                                            height: 'auto',
                                        }}
                                    />
                                    <div
                                        style={{
                                            width: 234,
                                        }}
                                    >
                                        <Picker />
                                    </div>
                                </div>
                            )}
                        />
                    </Form.Item>
                    <Form.Item label='' wrapperCol={{ offset: 3, span: 5 }}>
                        <Space size={'small'} style={{ float: 'right' }}>
                            <Button type='text' onClick={() => navigate(-1)}>
                                Отменить
                            </Button>
                            {canManage && (
                                <Button type='primary' htmlType='submit' loading={loading}>
                                    Сохранить
                                </Button>
                            )}
                        </Space>
                    </Form.Item>
                </Form>
            )}

            {contextHolder}
        </>
    );
};

export default TransportTariff;
